<script>
  // The want page's Wants tab (spec "The page" > Wants): the shelf of
  // bundles, then the picked bundle's cards (a sortable table or the binder
  // tiles) with numbered pages. Picking a bundle swaps the cards shown;
  // nothing expands. A Project's menu (Source Link, Remove) opens from its
  // tile. The owner writes here: the Default Want List's first row is Add a
  // card (a new card lands first until a column is sorted), each of its
  // cards opens its options (finish, set, printing; Remove), and the New
  // project tile opens its modal. Everything shown comes from the page's
  // store (stores/want-page.svelte.js), which also does the writes (`owner`,
  // null for a visitor: one value decides every owner-only control); this
  // tab only asks. The open options follow a re-point that merged their want
  // into another through the store's `survivorOf`.
  import BundleShelf from './BundleShelf.svelte';
  import ProjectMenu from './ProjectMenu.svelte';
  import WantTable from './WantTable.svelte';
  import WantTiles from './WantTiles.svelte';
  import AddCardRow from './AddCardRow.svelte';
  import CardOptionsModal from './CardOptionsModal.svelte';
  import NewProjectModal from './NewProjectModal.svelte';
  import Pagination from '../cardcollection/Pagination.svelte';
  import {
    wantRow,
    sortRows,
    nextSort,
    pageOf,
    recentFirst,
    projectSlotsLeft,
    DEFAULT_SORT,
  } from '../../lib/logic/want-page-logic.js';
  import { WANT_PAGE_SIZES } from '../../lib/logic/config.js';
  import {
    wantPage,
    ownerWrites,
    pickBundle,
    recentWants,
    clearRecent,
    survivorOf,
  } from '../../lib/stores/want-page.svelte.js';

  const owner = $derived(wantPage.isSelf ? ownerWrites : null);
  const bundles = $derived(wantPage.bundles);
  const display = $derived(wantPage.display);

  let sort = $state(DEFAULT_SORT);
  let menuId = $state(/** @type {string | null} */ (null));
  // The want whose options are open, as opened: a re-point that merges it
  // into another is followed through survivorOf.
  let optionsId = $state(/** @type {number | null} */ (null));
  let newProjectOpen = $state(false);
  let menuAnchor = $state(/** @type {HTMLElement | null} */ (null));
  let listTop = $state(/** @type {HTMLElement | null} */ (null));

  // The shelf always holds the Default Want List, so a vanished pick falls
  // back to it.
  const picked = $derived(bundles.find((b) => b.id === wantPage.pickedId) ?? bundles[0]);
  const projects = $derived(bundles.filter((b) => b.kind === 'project'));
  // Only the owner's Default Want List takes cards one by one; a Project's
  // come from its deck.
  const editable = $derived(!!owner && picked.kind === 'default');
  const rows = $derived(
    recentFirst(
      sortRows(picked.entries.map(wantRow), sort),
      picked.kind === 'default' ? recentWants() : []
    )
  );
  const optionsEntry = $derived.by(() => {
    if (!editable || optionsId == null) return null;
    const id = survivorOf(optionsId);
    return picked.entries.find((e) => e.id === id) ?? null;
  });
  const pageSize = $derived(display === 'list' ? WANT_PAGE_SIZES.list : WANT_PAGE_SIZES.cards);
  // The picked bundle's id as a plain value: a reload rebuilds every bundle
  // object, but this only changes when a different bundle is picked.
  const shownId = $derived(picked.id);
  // Page 1 again for a new bundle or page size (never for a reload); the
  // pager writes it.
  let page = $derived.by(() => {
    void shownId;
    void display;
    return 1;
  });
  const shown = $derived(pageOf(rows, page, pageSize));
  const menuBundle = $derived(projects.find((b) => b.id === menuId) ?? null);

  /** @param {import('../../lib/logic/want-page-logic.js').SortKey} key */
  function onSort(key) {
    sort = nextSort(sort, key);
    clearRecent(); // a chosen sort puts the new cards in their place
    page = 1;
  }

  /**
   * The Add a card row's add: the new card leads the list (the store keeps
   * it first), on page 1.
   * @param {string} name
   */
  async function addCard(name) {
    const id = owner ? await owner.addCard(name) : null;
    if (id == null) return false;
    page = 1;
    return true;
  }

  /** The Project menu's Remove: the menu closes, the page removes it. */
  function removeMenuProject() {
    const id = menuId;
    menuId = null;
    if (owner && id) owner.removeProject(id);
  }

  /** The card options' Remove: the dialog closes, the row goes at once. */
  function removeOptionsCard() {
    const id = optionsEntry?.id;
    optionsId = null;
    if (owner && id != null) owner.removeEntry(id);
  }

  /**
   * The card options' pick: the page re-points the entry (in pick order,
   * even once the options close).
   * @param {import('../../lib/logic/card-options-logic.js').Spec} spec
   * @returns {Promise<void>}
   */
  function setOptionsSpec(spec) {
    const id = optionsId;
    if (!owner || id == null) return Promise.resolve();
    return owner.setSpecificity(id, spec);
  }

  /** @param {number} p */
  function goToPage(p) {
    page = p;
    listTop?.scrollIntoView?.({ block: 'start' });
  }
</script>

<div class="wants">
  <BundleShelf
    {bundles}
    pickedId={shownId}
    onPick={pickBundle}
    onMenu={(id, anchor) => {
      menuId = id;
      menuAnchor = anchor;
    }}
    newProject={owner
      ? {
          slotsLeft: projectSlotsLeft(projects, wantPage.projectCap),
          onOpen: () => (newProjectOpen = true),
        }
      : null}
  />

  <section class="picked" aria-label={picked.name}>
    <div class="list-top" bind:this={listTop}></div>
    {#if rows.length || editable}
      {#if display === 'list'}
        <WantTable
          rows={shown.rows}
          {sort}
          {onSort}
          addRow={editable && shown.page === 1 ? addRow : undefined}
          onOpen={editable ? (row) => (optionsId = row.id) : undefined}
        />
      {:else}
        <WantTiles
          rows={shown.rows}
          addTile={editable && shown.page === 1 ? addTile : undefined}
          onOpen={editable ? (row) => (optionsId = row.id) : undefined}
        />
      {/if}
      {#if rows.length}
        <Pagination page={shown.page} total={rows.length} {pageSize} onPageChange={goToPage} />
      {/if}
    {:else if picked.kind === 'project'}
      <p class="empty">Nothing wanted from this project.</p>
    {:else}
      <p class="empty">No wants yet.</p>
    {/if}
  </section>
</div>

{#snippet addRow()}
  <AddCardRow onAdd={addCard} />
{/snippet}
{#snippet addTile()}
  <AddCardRow onAdd={addCard} compact />
{/snippet}

{#if menuBundle?.project}
  <ProjectMenu
    project={menuBundle.project}
    name={menuBundle.name}
    count={menuBundle.count}
    icon={menuBundle.site?.icon ?? null}
    anchor={menuAnchor}
    onRemove={owner ? removeMenuProject : null}
    onClose={() => (menuId = null)}
  />
{/if}

{#if optionsEntry}
  <CardOptionsModal
    entry={optionsEntry}
    onChange={setOptionsSpec}
    onRemove={removeOptionsCard}
    onClose={() => (optionsId = null)}
  />
{/if}

{#if owner && newProjectOpen}
  <NewProjectModal
    onAdd={async (link) => {
      const res = await owner.addProject(link);
      if (res.ok) newProjectOpen = false;
      return res;
    }}
    onClose={() => (newProjectOpen = false)}
  />
{/if}

<style>
  .wants {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .picked {
    display: flex;
    flex-direction: column;
  }
  .list-top {
    scroll-margin-top: 72px;
  }
  .empty {
    margin: 1.5rem 0;
    text-align: center;
    color: var(--color-text-tertiary);
  }
</style>
